// D:\my-website\src\App.js

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 用于国际化

// 导入 LanguageSwitcher 组件
import LanguageSwitcher from './LanguageSwitcher'; // <--- 确保这一行存在且路径正确！

// 导入 Firebase db 实例 和 Firestore 操作函数
// 确保您的 src/firebase.js 文件已正确设置并导出了 db
import { db } from './firebase';
import { doc, onSnapshot, setDoc } from 'firebase/firestore'; // 导入 Firestore 的 doc, onSnapshot, setDoc 函数

// 导入页面组件
import HomePage from './components/HomePage';
import AboutUsPage from './components/AboutUsPage';
import BusinessOutlookPage from './components/BusinessOutlookPage';
import IndustryDynamicsPage from './components/IndustryDynamicsPage';
import ContactUsPage from './components/ContactUsPage';
import AdminDashboard from './components/AdminDashboard'; // 管理后台组件

// 导入布局组件
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// 导入默认内容数据 - 确保路径正确
// 如果您没有 defaultContent.js 文件，请根据您的项目实际情况调整或创建
import {
    defaultHomepageSettings,
    defaultAboutUsContent,
    defaultBusinessOutlookContent,
    defaultIndustryDynamicsContent,
    defaultContactInfo
} from './utils/defaultContent'; // 确保这里的路径是 './utils/defaultContent'

function App() {
  const { t } = useTranslation(); // 国际化钩子

  // 网站内容的 state
  const [homepageSettings, setHomepageSettings] = useState(null);
  const [aboutUsContent, setAboutUsContent] = useState(null);
  const [businessOutlookContent, setBusinessOutlookContent] = useState(null);
  const [industryDynamicsContent, setIndustryDynamicsContent] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true); // 全局加载状态
  const [error, setError] = useState(null);     // 全局错误状态

  // --- Firestore 数据获取 ---

  // 获取主页设置
  useEffect(() => {
    // 确保全局变量为本地开发定义，如果环境未提供
    // 这些通常由 Canvas 环境注入。
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local';

    const docRef = doc(db, `artifacts/${appId}/public/data/homepage_settings`, 'main_settings');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setHomepageSettings(docSnap.data());
      } else {
        console.warn("主页设置文档未找到！尝试设置默认值...");
        // 如果文档不存在，可以尝试设置一个默认值，或者在管理后台创建
        setHomepageSettings(defaultHomepageSettings); // 使用导入的默认值
        setError(t('no_homepage_settings')); // 设置错误信息
      }
      setLoading(false);
    }, (err) => {
      console.error(t('fetch_homepage_settings_error'), err);
      setError(t('error_loading_homepage_settings'));
      setHomepageSettings(defaultHomepageSettings); // 加载失败也使用默认值
      setLoading(false);
    });

    // 清理函数，组件卸载时取消订阅
    return () => unsubscribe();
  }, [t]); // 依赖 t，以便在语言切换时更新错误信息

  // 获取关于我们内容
  useEffect(() => {
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local';
    const docRef = doc(db, `artifacts/${appId}/public/data/about_us_content`, 'main_content');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setAboutUsContent(docSnap.data());
      } else {
        setAboutUsContent(defaultAboutUsContent); // 使用导入的默认值
      }
    }, (err) => {
      console.error("获取关于我们内容失败:", err);
      setAboutUsContent(defaultAboutUsContent); // 加载失败也使用默认值
    });
    return () => unsubscribe();
  }, []);

  // 获取业务与展望内容
  useEffect(() => {
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local';
    const docRef = doc(db, `artifacts/${appId}/public/data/business_outlook_content`, 'main_content');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setBusinessOutlookContent(docSnap.data());
      } else {
        setBusinessOutlookContent(defaultBusinessOutlookContent); // 使用导入的默认值
      }
    }, (err) => {
      console.error("获取业务与展望内容失败:", err);
      setBusinessOutlookContent(defaultBusinessOutlookContent); // 加载失败也使用默认值
    });
    return () => unsubscribe();
  }, []);

  // 获取行业动态内容
  useEffect(() => {
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local';
    const docRef = doc(db, `artifacts/${appId}/public/data/industry_dynamics_content`, 'main_content');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setIndustryDynamicsContent(docSnap.data());
      } else {
        setIndustryDynamicsContent(defaultIndustryDynamicsContent); // 使用导入的默认值
      }
    }, (err) => {
      console.error("获取行业动态内容失败:", err);
      setIndustryDynamicsContent(defaultIndustryDynamicsContent); // 加载失败也使用默认值
    });
    return () => unsubscribe();
  }, []);

  // 获取联系信息
  useEffect(() => {
    const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : 'default-app-id-for-local';
    const docRef = doc(db, `artifacts/${appId}/public/data/contact_info`, 'main_contact');
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        setContactInfo(docSnap.data());
      } else {
        setContactInfo(defaultContactInfo); // 使用导入的默认值
      }
    }, (err) => {
      console.error("获取联系信息失败:", err);
      setContactInfo(defaultContactInfo); // 加载失败也使用默认值
    });
    return () => unsubscribe();
  }, []);

  // --- 加载和错误处理 ---
  // 这里可以根据需要调整加载状态的判断逻辑，例如等待所有数据都加载完成
  if (loading || !homepageSettings || !aboutUsContent || !businessOutlookContent || !industryDynamicsContent || !contactInfo) {
    return (
      <div className="flex items-center justify-center min-h-screen text-2xl text-gray-700">
        {t('loading_content')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-red-600 text-xl p-4">
        <p>{error}</p>
        <p>请确保 Firebase 配置正确，并且 Firestore 中存在 'settings' 集合和相应的文档。</p>
        <p>如果这是新项目，您可能需要先通过管理后台添加一些内容。</p>
      </div>
    );
  }

  // 根据环境设置 basename
  const routerBasename = process.env.NODE_ENV === 'production' ? '/website' : '/';

  // --- 渲染应用 ---
  return (
    <Router basename={routerBasename}> {/* 根据环境动态设置 basename */}
      <div className="App flex flex-col min-h-screen">
        {/* LanguageSwitcher 组件，假设它在 src/LanguageSwitcher.js */}
        <LanguageSwitcher /> 
        <Navbar setCurrentPage={() => {}} logoUrl={homepageSettings?.logoUrl} /> {/* Navbar 接收 setCurrentPage 和 logoUrl */}

        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<HomePage homepageData={homepageSettings} />} />
            <Route path="/about" element={<AboutUsPage aboutUsContent={aboutUsContent} />} />
            <Route path="/business" element={<BusinessOutlookPage businessOutlookContent={businessOutlookContent} />} />
            <Route path="/industry" element={<IndustryDynamicsPage industryDynamicsContent={industryDynamicsContent} />} />
            <Route path="/contact" element={<ContactUsPage contactInfo={contactInfo} />} />
            {/* 管理后台路由 */}
            <Route
              path="/admin"
              element={
                <AdminDashboard
                  homepageSettings={homepageSettings}
                  setHomepageSettings={setHomepageSettings}
                  aboutUsContent={aboutUsContent}
                  setAboutUsContent={setAboutUsContent}
                  businessOutlookContent={businessOutlookContent}
                  setBusinessOutlookContent={setBusinessOutlookContent}
                  industryDynamicsContent={industryDynamicsContent}
                  setIndustryDynamicsContent={setIndustryDynamicsContent}
                  contactInfo={contactInfo}
                  setContactInfo={setContactInfo}
                />
              }
            />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
